import { ReactNode } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { BoldLabel, LightGrayLabel } from 'components/labels';
import { DeleteButton } from './DeleteButton';
import { EditButton } from './EditButton';
import { ContentBox, Wrapper } from './styledComponents';

export interface DetailRow {
  label?: string;
  value: ReactNode;
  variant?: 'bold' | 'light' | 'custom';
  sx?: SxProps<Theme>;
}

export interface DetailItemProps {
  title: string;
  subtitle?: string;
  details?: DetailRow[];
  children?: ReactNode;
  sx?: SxProps<Theme>;
  onEditItem?: () => void;
  onDeleteItem?: () => void;
}

const renderDetailValue = (detail: DetailRow) => {
  switch (detail.variant) {
    case 'bold':
      return <BoldLabel>{detail.value}</BoldLabel>;
    case 'custom':
      return detail.value;
    default:
      return (
        <LightGrayLabel variant="body2">
          {detail.label && `${detail.label}: `}
          {detail.value}
        </LightGrayLabel>
      );
  }
};

export const DetailItem = ({
  title,
  subtitle,
  details = [],
  children,
  sx,
  onEditItem,
  onDeleteItem
}: DetailItemProps) => {
  const hasActions = onEditItem || onDeleteItem;

  return (
    <Wrapper sx={sx}>
      <ContentBox>
        <Box sx={{ pb: 1 }}>
          <BoldLabel>{title}</BoldLabel>
          {subtitle && (
            <LightGrayLabel sx={{ pb: 1 }} variant="body2">
              {subtitle}
            </LightGrayLabel>
          )}
          {details.map((detail, index) => (
            <Box key={index} sx={{ pb: 1, ...(detail.sx || {}) }}>
              {renderDetailValue(detail)}
            </Box>
          ))}
          {hasActions && (
            <Box
              sx={{
                pt: 1,
                display: 'flex',
                gap: 1,
                alignContent: 'center',
                justifyContent: 'flex-end'
              }}
            >
              {onDeleteItem && <DeleteButton onDeleteItem={onDeleteItem} />}
              {onEditItem && <EditButton onEditItem={onEditItem} />}
            </Box>
          )}
          {children}
        </Box>
      </ContentBox>
    </Wrapper>
  );
};
